import React, { useState } from "react";
import { Plus, X, Loader2, Users, Mail, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PRESETS = ["Passport Copy", "Proof of Address", "Bank Statement", "Utility Bill"];

export default function BulkDocRequest() {
  const [clients, setClients] = useState([{ email: "", name: "" }]);
  const [docs, setDocs] = useState(["Passport Copy", "Proof of Address"]);
  const [docInput, setDocInput] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const setClient = (i, k, v) => setClients((c) => c.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addClient = () => setClients((c) => [...c, { email: "", name: "" }]);
  const removeClient = (i) => setClients((c) => c.filter((_, idx) => idx !== i));

  const addDoc = (name) => {
    const n = (name || "").trim();
    if (!n || docs.some((d) => d.toLowerCase() === n.toLowerCase())) return;
    setDocs((d) => [...d, n]); setDocInput("");
  };
  const removeDoc = (i) => setDocs((d) => d.filter((_, x) => x !== i));

  const submit = async () => {
    const valid = clients.filter((c) => c.email && c.email.includes("@"));
    if (valid.length === 0) return toast.error("Add at least one valid client email.");
    if (docs.length === 0) return toast.error("Add at least one document.");
    setSending(true); setResults(null);
    try {
      const res = await api.post("/admin/upload-links/bulk", { clients: valid, documents: docs, message });
      setResults(res.data);
      toast.success(`Generated ${res.data.count} link(s) and emailed each client.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk request failed."); } finally { setSending(false); }
  };

  const copy = (u) => { navigator.clipboard?.writeText(u); toast.success("Link copied."); };

  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50/40 p-5 space-y-4" data-testid="bulk-doc-request">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-orange-700" />
        <h3 className="font-heading text-base text-foreground">Bulk document request</h3>
        <span className="text-xs text-muted-foreground">— send the same checklist to several clients at once</span>
      </div>

      {/* Clients */}
      <div className="space-y-2">
        {clients.map((c, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`bulk-client-${i}`}>
            <Input value={c.email} onChange={(e) => setClient(i, "email", e.target.value)} placeholder="Client email" className="h-8 bg-card border-border text-sm" data-testid={`bulk-email-${i}`} />
            <Input value={c.name} onChange={(e) => setClient(i, "name", e.target.value)} placeholder="Client name (optional)" className="h-8 bg-card border-border text-sm" data-testid={`bulk-name-${i}`} />
            {clients.length > 1 && <button onClick={() => removeClient(i)} className="text-muted-foreground hover:text-red-600 shrink-0" data-testid={`bulk-remove-client-${i}`}><X className="h-4 w-4" /></button>}
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addClient} className="h-7 rounded-sm border-slate-300 bg-card text-xs" data-testid="bulk-add-client"><Plus className="h-3.5 w-3.5 mr-1" /> Add client</Button>
      </div>

      {/* Docs */}
      <div className="flex flex-wrap gap-1.5">
        {docs.map((d, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-sm border border-orange-200 bg-orange-100 px-2 py-1 text-xs text-orange-800" data-testid={`bulk-chip-${i}`}>
            {d}<button onClick={() => removeDoc(i)} className="hover:text-red-600"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <Input value={docInput} onChange={(e) => setDocInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDoc(docInput))} placeholder="Add a document name…" className="h-8 bg-card border-border text-sm" data-testid="bulk-doc-input" />
        <Button size="sm" variant="outline" onClick={() => addDoc(docInput)} className="h-8 rounded-sm border-slate-300 bg-card text-xs shrink-0" data-testid="bulk-doc-add"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.filter((p) => !docs.some((d) => d.toLowerCase() === p.toLowerCase())).map((p) => (
          <button key={p} onClick={() => addDoc(p)} className="rounded-sm border border-dashed border-slate-300 bg-card px-2 py-0.5 text-[11px] text-muted-foreground hover:border-orange-300 hover:text-orange-700" data-testid={`bulk-preset-${p.replace(/\s+/g, "-")}`}>+ {p}</button>
        ))}
      </div>

      <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Shared message to all clients (optional)" className="h-8 bg-card border-border text-sm" data-testid="bulk-message" />

      <Button size="sm" disabled={sending} onClick={submit} className="rounded-sm bg-primary text-primary-foreground h-8 text-xs" data-testid="bulk-generate">
        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="h-3.5 w-3.5 mr-1" /> Generate & email links</>}
      </Button>

      {results && (
        <div className="space-y-1.5 pt-1" data-testid="bulk-results">
          <p className="text-xs font-medium text-foreground">{results.count} link(s) created:</p>
          {results.created.map((r) => (
            <div key={r.token} className="flex items-center gap-2 rounded-sm border border-border bg-card px-2 py-1" data-testid={`bulk-result-${r.token}`}>
              {r.emailed ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" /> : <Mail className="h-3.5 w-3.5 text-amber-600 shrink-0" />}
              <span className="text-xs text-foreground truncate flex-1">{r.client_email}{r.client_name ? ` · ${r.client_name}` : ""}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.emailed ? "emailed" : "not emailed"}</span>
              <button onClick={() => copy(r.public_url)} className="text-muted-foreground hover:text-orange-700 shrink-0"><Copy className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
